import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchemaIcon from '@mui/icons-material/Schema';

const MAX_VISIBLE_RESULT_ROWS = 50;

// Temporary client-side date standardization. The invoices date fields are stored
// as inconsistent strings (JS Date.toString() AND ISO). Until they are normalized
// in the database, render any date-like value in one consistent, readable format.
const DATE_LIKE = /^\s*(\d{4}-\d{2}-\d{2}T|[A-Z][a-z]{2}\s[A-Z][a-z]{2}\s\d{1,2}\s\d{4})/;
const formatMaybeDate = (value) => {
    if (typeof value !== 'string' || !DATE_LIKE.test(value))
        return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return null;
    const atMidnight = parsed.getHours() === 0 && parsed.getMinutes() === 0 && parsed.getSeconds() === 0;
    return parsed.toLocaleString(undefined, atMidnight
        ? { year: 'numeric', month: 'short', day: 'numeric' }
        : { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// IDs are for internal relationship linking — never shown to the end user in a
// report. Detect id-like keys and raw ObjectId hash values so we can drop them.
const isIdKey = (key) => key === '_id' || key === 'id' || /_?id$/i.test(key);
const OBJECT_ID = /^[a-f0-9]{24}$/i;

// "availableDate" -> "Available Date", "property_type" -> "Property Type".
const humanizeHeader = (key) => String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

// A scalar as display text: date-standardized, and ObjectId hashes hidden.
const scalarText = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') {
        if (OBJECT_ID.test(value)) return '';
        return formatMaybeDate(value) || value;
    }
    if (typeof value === 'object') return '';
    return String(value);
};

// Summarize one object as "Name: value" pairs, skipping id keys and nested objects.
const objectSummary = (obj) => {
    if (obj && typeof obj === 'object' && 'name' in obj && 'value' in obj)
        return `${obj.name}: ${scalarText(obj.value)}`;
    return Object.entries(obj || {})
        .filter(([key, val]) => !isIdKey(key) && val != null && typeof val !== 'object')
        .map(([key, val]) => {
            const text = scalarText(val);
            return text ? `${humanizeHeader(key)}: ${text}` : null;
        })
        .filter(Boolean)
        .join(', ');
};

// Turn any cell value into readable lines: arrays/objects are expanded instead of
// dumped as raw JSON, and id hashes are stripped throughout.
const cellLines = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((item) => (item && typeof item === 'object' ? objectSummary(item) : scalarText(item)))
            .filter(Boolean);
    }
    if (value && typeof value === 'object') {
        const summary = objectSummary(value);
        return summary ? [summary] : [];
    }
    const text = scalarText(value);
    return text ? [text] : [];
};

// Stable, logical column ordering so related tables read the same way regardless
// of the order the model wrote its $project stage.
const columnRank = (key) => {
    const k = String(key).toLowerCase();
    if (/name$|^name|title|number|code/.test(k)) return 0;
    if (/status|state/.test(k)) return 1;
    if (/type|category|kind|class/.test(k)) return 2;
    if (/area|location|zone|region|site/.test(k)) return 3;
    if (/date|due|created|updated|expir|start|end/.test(k)) return 4;
    if (/email|phone|contact/.test(k)) return 5;
    if (/rent|amount|total|sum|price|balance|cost|count|qty|quantity|avg/.test(k)) return 6;
    if (/desc|note|remark|comment/.test(k)) return 8;
    return 7;
};

const orderColumns = (cols, rows) => cols
    .map((col, index) => ({
        col,
        index,
        rank: rows.some((row) => Array.isArray(row?.[col])) ? 9 : columnRank(col),
    }))
    .sort((a, b) => (a.rank - b.rank) || (a.index - b.index))
    .map((entry) => entry.col);

// Non-LLM, pandas-style summary of a result set: numeric columns get sum/avg/
// min/max; low-cardinality categorical columns get value counts.
const summarizeRows = (rows, columns) => {
    const summaries = [];
    columns.forEach((col) => {
        const raw = rows.map((row) => row?.[col]).filter((v) => v != null && v !== '');
        if (raw.length === 0 || raw.some((v) => typeof v === 'object'))
            return;
        const nums = raw.filter((v) => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))));
        if (nums.length === raw.length) {
            const vals = nums.map(Number);
            const sum = vals.reduce((acc, n) => acc + n, 0);
            summaries.push({
                column: col,
                kind: 'numeric',
                count: vals.length,
                sum,
                avg: sum / vals.length,
                min: Math.min(...vals),
                max: Math.max(...vals),
            });
            return;
        }
        const counts = new Map();
        raw.forEach((v) => {
            const key = scalarText(v) || String(v);
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        if (counts.size >= 1 && counts.size <= 8 && counts.size < raw.length) {
            summaries.push({
                column: col,
                kind: 'category',
                counts: Array.from(counts.entries()).sort((a, b) => b[1] - a[1]),
            });
        }
    });
    return summaries;
};

const fmtNum = (n) => Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

// Compact label for a resolved-value entry: single-key objects collapse to just
// their value, and date-like values are standardized.
const resolvedLabel = (value) => {
    if (value && typeof value === 'object') {
        const entries = Object.entries(value);
        if (entries.length === 1) {
            const inner = entries[0][1];
            return formatMaybeDate(inner) || (typeof inner === 'object' ? JSON.stringify(inner) : String(inner));
        }
        return JSON.stringify(value);
    }
    return formatMaybeDate(value) || String(value);
};

const statusColor = (status) => {
    if (status === 'completed')
        return 'success';
    if (status === 'failed')
        return 'error';
    return 'info';
};

export const normalizeToolCall = (toolCall = {}) => ({
    ...toolCall,
    collection: toolCall.collection || toolCall.arguments?.collection,
    pipeline: toolCall.pipeline || toolCall.arguments?.pipeline,
    data: toolCall.data || toolCall.result?.data,
    result: toolCall.result || (
        toolCall.row_count != null || toolCall.rows || toolCall.error
            ? {
                row_count: toolCall.row_count,
                limited: toolCall.limited,
                rows: toolCall.rows || [],
                warnings: toolCall.warnings || [],
                error: toolCall.error,
            }
            : null
    ),
});

export const messageToolCalls = (message) => (
    message?.tool_calls || message?.metadata?.extra?.tool_calls || []
).map(normalizeToolCall);

const isSchemaCall = (toolCall) => (
    toolCall?.name === 'describe_schema' || !!toolCall?.data
);

const stageName = (stage) => Object.keys(stage || {})[0] || 'stage';

const queryPreview = (toolCall) => {
    if (toolCall.sql)
        return { language: 'SQL', code: toolCall.sql };
    if (toolCall.query)
        return { language: 'Query', code: toolCall.query };

    const collection = toolCall.collection || 'collection';
    const pipeline = toolCall.pipeline || [];
    return {
        language: 'MongoDB aggregation',
        code: `db.getCollection(${JSON.stringify(collection)}).aggregate(\n${JSON.stringify(pipeline, null, 2)}\n)`,
    };
};

const resultColumns = (result) => {
    if (result?.columns?.length)
        return result.columns;

    const columns = new Set();
    (result?.rows || []).forEach((row) => {
        Object.keys(row || {}).forEach((key) => columns.add(key));
    });
    return Array.from(columns);
};

// A result cell: expanded readable lines (nested arrays/objects flattened, id
// hashes stripped) rather than a raw JSON blob.
const ResultCell = React.memo(function ResultCell({ value }) {
    const lines = cellLines(value);
    if (lines.length === 0)
        return null;
    const shown = lines.slice(0, 5);
    return (
        <Stack spacing={0.25}>
            {shown.map((line, index) => (
                <Typography key={index} variant="body2" sx={{ fontSize: 13, lineHeight: 1.4 }}>
                    {line.length > 200 ? `${line.slice(0, 200)}...` : line}
                </Typography>
            ))}
            {lines.length > shown.length && (
                <Typography variant="caption" color="text.secondary">
                    +{lines.length - shown.length} more
                </Typography>
            )}
        </Stack>
    );
});

const QueryCodeBlock = React.memo(function QueryCodeBlock({ toolCall }) {
    const preview = queryPreview(toolCall);
    const pipeline = toolCall.pipeline || [];
    const [open, setOpen] = React.useState(false);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.75 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">Generated query</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {pipeline.length > 0 ? `${pipeline.length}-stage ` : ''}{preview.language}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Button size="small" startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setOpen((value) => !value)}>
                        {open ? 'Hide' : 'Show'}
                    </Button>
                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => navigator.clipboard?.writeText(preview.code)}>
                        Copy
                    </Button>
                </Stack>
            </Stack>
            {pipeline.length > 0 && (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ px: 1.5, pb: 1 }}>
                    {pipeline.map((stage, index) => (
                        <Chip
                            key={`${stageName(stage)}-${index}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                            label={`${index + 1}. ${stageName(stage)}`}
                        />
                    ))}
                </Stack>
            )}
            <Collapse in={open} unmountOnExit>
                <Divider />
                <Box
                    component="pre"
                    sx={{
                        bgcolor: 'grey.900',
                        color: 'grey.100',
                        fontSize: 12,
                        lineHeight: 1.6,
                        m: 0,
                        maxHeight: 320,
                        overflow: 'auto',
                        p: 2,
                        whiteSpace: 'pre',
                    }}
                >
                    {preview.code}
                </Box>
            </Collapse>
        </Paper>
    );
});

const SchemaPanel = React.memo(function SchemaPanel({ data }) {
    const fields = data?.fields || [];
    const resolvedValues = data?.resolved_values || [];

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.75 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <SchemaIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" noWrap>
                            {data?.collection || 'Schema'}
                        </Typography>
                    </Stack>
                    {data?.description && (
                        <Typography variant="caption" color="text.secondary">
                            {data.description}
                        </Typography>
                    )}
                </Box>
                <Chip size="small" color="info" label={`${fields.length} fields`} />
            </Stack>
            <Divider />
            {fields.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                    No field definitions were returned.
                </Typography>
            ) : (
                <TableContainer sx={{ maxHeight: 420 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {['Field', 'Label', 'Type', 'Description', 'Domain key'].map((column) => (
                                    <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {column}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fields.map((field, index) => (
                                <TableRow key={field.path || index} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                                        {field.path}
                                        {field.pii_redacted && (
                                            <Chip size="small" color="warning" label="PII" sx={{ ml: 0.5, height: 16, fontSize: 10 }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>{field.label}</TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                        <Chip size="small" variant="outlined" label={field.type || 'unknown'} />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 320, overflowWrap: 'anywhere', verticalAlign: 'top' }}>
                                        {field.description}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', verticalAlign: 'top' }}>{field.domain_key}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {resolvedValues.length > 0 && (
                <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Resolved values
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {resolvedValues.map((value, index) => (
                            <Chip
                                key={`${typeof value === 'object' ? JSON.stringify(value) : value}-${index}`}
                                size="small"
                                variant="outlined"
                                label={resolvedLabel(value)}
                            />
                        ))}
                    </Stack>
                </Box>
            )}
        </Paper>
    );
});

// Auto-computed summary panel (no LLM) shown beneath a result table.
const ResultSummary = React.memo(function ResultSummary({ rows, columns }) {
    const summaries = React.useMemo(() => summarizeRows(rows, columns), [rows, columns]);
    if (rows.length < 2 || summaries.length === 0)
        return null;
    return (
        <Box sx={{ px: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
                Quick summary · {rows.length.toLocaleString()} rows
            </Typography>
            <Stack spacing={0.75}>
                {summaries.map((summary) => (
                    <Box key={summary.column}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {humanizeHeader(summary.column)}
                        </Typography>
                        {summary.kind === 'numeric' ? (
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.25 }}>
                                <Chip size="small" variant="outlined" label={`avg ${fmtNum(summary.avg)}`} />
                                <Chip size="small" variant="outlined" label={`total ${fmtNum(summary.sum)}`} />
                                <Chip size="small" variant="outlined" label={`min ${fmtNum(summary.min)}`} />
                                <Chip size="small" variant="outlined" label={`max ${fmtNum(summary.max)}`} />
                            </Stack>
                        ) : (
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.25 }}>
                                {summary.counts.map(([value, count]) => (
                                    <Chip key={value} size="small" variant="outlined" label={`${value} × ${count}`} />
                                ))}
                            </Stack>
                        )}
                    </Box>
                ))}
            </Stack>
        </Box>
    );
});

const ResultTable = React.memo(function ResultTable({ result }) {
    const rows = result?.rows || [];
    const visibleRows = rows.slice(0, MAX_VISIBLE_RESULT_ROWS);
    // Drop id-like columns, and columns whose every value is an id hash / empty
    // after cleaning (e.g. unresolved ObjectId references) — never show hashes.
    const cleaned = resultColumns({ ...result, rows: visibleRows })
        .filter((column) => !isIdKey(column))
        .filter((column) => visibleRows.some((row) => cellLines(row?.[column]).length > 0));
    const columns = orderColumns(cleaned, visibleRows);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2">Query results</Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                    <Chip size="small" color="success" label={`${result?.row_count || 0} rows`} />
                    {result?.limited && <Chip size="small" color="warning" label="Result limited" />}
                </Stack>
            </Stack>
            <Divider />
            {(result?.warnings || []).length > 0 && (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 0, border: 0, borderBottom: 1, borderColor: 'warning.light' }}>
                    {result.warnings.join(' ')}
                </Alert>
            )}
            {Number(result?.row_count || 0) > visibleRows.length && (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 0, border: 0, borderBottom: 1, borderColor: 'info.light' }}>
                    Showing the first {visibleRows.length.toLocaleString()} stored rows to keep this view responsive.
                </Alert>
            )}
            {result?.error ? (
                <Alert severity="error">{result.error}</Alert>
            ) : columns.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                    The query completed without returning rows.
                </Typography>
            ) : (
                <TableContainer sx={{ maxHeight: 420 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {humanizeHeader(column)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map((row, rowIndex) => (
                                <TableRow key={row._id || row.id || rowIndex} hover>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column}
                                            sx={{ maxWidth: 360, overflowWrap: 'anywhere', verticalAlign: 'top' }}
                                        >
                                            <ResultCell value={row?.[column]} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {!result?.error && columns.length > 0 && (
                <ResultSummary rows={rows} columns={columns} />
            )}
        </Paper>
    );
});

const ToolCallCard = React.memo(function ToolCallCard({ toolCall, index }) {
    const normalized = normalizeToolCall(toolCall);
    const schema = isSchemaCall(normalized);
    const status = schema
        ? (normalized.data ? 'completed' : 'running')
        : (normalized.result
            ? (normalized.result.error ? 'failed' : 'completed')
            : 'running');

    return (
        <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">
                    {schema ? 'Schema lookup' : `Data retrieval ${index + 1}`}: {normalized.name || 'Unknown tool'}
                </Typography>
                <Chip size="small" color={statusColor(status)} label={status} />
            </Stack>
            {schema ? (
                normalized.data ? (
                    <SchemaPanel data={normalized.data} />
                ) : (
                    <Alert severity="info">Waiting for the schema description...</Alert>
                )
            ) : (
                <>
                    <QueryCodeBlock toolCall={normalized} />
                    {normalized.result ? (
                        <ResultTable result={normalized.result} />
                    ) : (
                        <Alert severity="info">Waiting for the query execution result...</Alert>
                    )}
                </>
            )}
        </Stack>
    );
});

export default React.memo(function AIQueryResults({ toolCalls = [] }) {
    if (!toolCalls.length)
        return null;

    return (
        <Stack spacing={1.25}>
            {toolCalls.map((toolCall, index) => (
                <ToolCallCard
                    key={toolCall.call_id || `${toolCall.name || 'tool'}-${index}`}
                    toolCall={toolCall}
                    index={index}
                />
            ))}
        </Stack>
    );
});
